import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ShieldCheck, 
  FileText, 
  Building2, 
  Home,
  Scale,
  FileCheck,
  Phone,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const ServiceCard = ({ icon: Icon, title, description, whoFor, whyUs, features }) => (
  <Card className="bg-white border border-slate-100 rounded-2xl p-8 md:p-12 hover:border-amber-700/30 transition-all duration-300 hover:shadow-lg">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center shrink-0">
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-slate-900 mb-2">{title}</h2>
            <p className="text-slate-600">{description}</p>
          </div>
        </div>
        <div className="mb-6">
          <h3 className="font-semibold text-slate-900 mb-2">Who It's For</h3>
          <p className="text-slate-600">{whoFor}</p>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 mb-2">Why Work With Me</h3>
          <p className="text-slate-600">{whyUs}</p>
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4">What's Included</h3>
        <ul className="space-y-3">
          {features}
        </ul>
      </div>
    </div>
  </Card>
);

const FeatureItem = ({ text }) => (
  <li className="flex items-start gap-3">
    <CheckCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
    <span className="text-slate-600 text-sm">{text}</span>
  </li>
);

export const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Services</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-slate-900 mb-6 max-w-3xl mx-auto" data-testid="services-title">
            Title Services That Make Closings Simple
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            From straightforward home purchases to complex commercial transactions, I provide the expertise and personal attention your deal deserves.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
          <ServiceCard
            icon={Home}
            title="Residential Title Insurance"
            description="Protecting homebuyers and sellers with comprehensive title coverage."
            whoFor="First-time buyers, sellers, and real estate agents"
            whyUs="I personally review every residential file, catching potential issues early and keeping you informed throughout the process."
            features={<><FeatureItem text="Full title search and examination" /><FeatureItem text="Lien and judgment searches" /><FeatureItem text="Owner and lender policies" /><FeatureItem text="Clear communication at every step" /></>}
          />
          <ServiceCard
            icon={Building2}
            title="Commercial Title Insurance"
            description="Comprehensive protection for commercial real estate transactions."
            whoFor="Investors, developers, business owners, and commercial lenders"
            whyUs="Commercial deals are complex. I bring the experience needed to navigate zoning issues, entity structures, and multi-party transactions smoothly."
            features={<><FeatureItem text="Complex title examination" /><FeatureItem text="Survey review and endorsements" /><FeatureItem text="Entity verification" /><FeatureItem text="Large transaction handling" /></>}
          />
          <ServiceCard
            icon={FileText}
            title="Settlement & Closing Services"
            description="Professional, stress-free closings for all parties."
            whoFor="Buyers, sellers, agents, attorneys, and lenders"
            whyUs="I coordinate with all parties to ensure your closing is on time and hassle-free."
            features={<><FeatureItem text="Scheduling and coordination" /><FeatureItem text="Document preparation" /><FeatureItem text="Funds disbursement" /><FeatureItem text="Recording services" /></>}
          />
          <ServiceCard
            icon={ShieldCheck}
            title="Investor & Builder Transactions"
            description="Specialized support for high-volume and development projects."
            whoFor="Real estate investors, builders, and developers"
            whyUs="Coming from a builder family, I understand the unique needs of investors and developers."
            features={<><FeatureItem text="Quick turnaround for investors" /><FeatureItem text="New construction closings" /><FeatureItem text="Subdivision work" /><FeatureItem text="Volume pricing available" /></>}
          />
          <ServiceCard
            icon={Scale}
            title="Probate & Inherited Property"
            description="Sensitive handling of estate and inheritance closings."
            whoFor="Estate executors, heirs, and probate attorneys"
            whyUs="These situations require patience and expertise. I help families navigate with compassion."
            features={<><FeatureItem text="Estate title searches" /><FeatureItem text="Heir verification" /><FeatureItem text="Probate coordination" /><FeatureItem text="Clear explanation of process" /></>}
          />
          <ServiceCard
            icon={FileCheck}
            title="Deed Transfers & Special Situations"
            description="Handling unique and complex title situations."
            whoFor="Anyone with non-standard title needs"
            whyUs="From quit claim deeds to resolving old liens, I have the experience to handle complex situations."
            features={<><FeatureItem text="Deed preparation and recording" /><FeatureItem text="Title curative work" /><FeatureItem text="Lien resolution" /><FeatureItem text="Name changes and corrections" /></>}
          />
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Need Help With Your Transaction?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Every deal is different. Let's talk about what you need and how I can help make your closing smooth and stress-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="services-cta-primary">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all hover:scale-105">
                <Phone className="w-5 h-5 mr-2" />
                Start a Conversation
              </Button>
            </Link>
            <Link to="/faq" data-testid="services-cta-secondary">
              <Button variant="outline" className="border-2 border-slate-200 hover:border-slate-900 rounded-full px-8 py-4 font-medium">
                Common Questions
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
